import React, { useEffect, useRef, useState } from 'react';
import { Bell, CheckCheck, Banknote, ShieldOff, FileCheck, FileX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from '@/contasis';
import { Button } from '@/contasis';
import { useNotificaciones } from '@/contexts/ContextoNotificaciones';
import type { Notificacion, TipoNotificacion } from '@/modelos/notificaciones/notificacion.types';

const TIPO_CONFIG: Record<TipoNotificacion, { icon: React.ReactNode; accent: string; iconBg: string }> = {
  CAJA_ABIERTA:        { icon: <Banknote  className="w-3.5 h-3.5" />, accent: 'border-emerald-400', iconBg: 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400' },
  CAJA_CERRADA:        { icon: <ShieldOff className="w-3.5 h-3.5" />, accent: 'border-slate-300 dark:border-slate-600',  iconBg: 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400' },
  COMPROBANTE_EMITIDO: { icon: <FileCheck className="w-3.5 h-3.5" />, accent: 'border-blue-400',   iconBg: 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400' },
  COMPROBANTE_ANULADO: { icon: <FileX     className="w-3.5 h-3.5" />, accent: 'border-red-400',    iconBg: 'bg-red-100 text-red-500 dark:bg-red-900/40 dark:text-red-400' },
};

function parseFechaBackend(fechaIso: string): Date {
  if (!fechaIso.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(fechaIso)) {
    return new Date(`${fechaIso}-05:00`);
  }
  return new Date(fechaIso);
}

function tiempoRelativo(fechaIso: string): string {
  const diff = Math.max(0, Date.now() - parseFechaBackend(fechaIso).getTime());
  const seg  = Math.floor(diff / 1_000);
  if (seg < 60)  return seg <= 5 ? 'Ahora' : `${seg} seg`;
  const min = Math.floor(seg / 60);
  if (min < 60)  return `${min} min`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24)  return `${hrs} h`;
  const dias = Math.floor(hrs / 24);
  return dias === 1 ? '1 día' : `${dias} días`;
}

function ItemNotificacion({
  notificacion,
  onMarcarLeida,
}: {
  notificacion: Notificacion;
  onMarcarLeida: (id: string) => void;
}) {
  const isUnread = !notificacion.fueLeida;
  const config   = TIPO_CONFIG[notificacion.tipo] ?? TIPO_CONFIG.CAJA_CERRADA;

  return (
    <button
      type="button"
      onClick={() => { if (isUnread) onMarcarLeida(notificacion.id); }}
      className={`w-full text-left flex items-start gap-3 px-4 py-3 border-l-2 ${config.accent} hover:bg-slate-50 dark:hover:bg-gray-700/50 transition-colors ${isUnread ? 'bg-slate-50/80 dark:bg-gray-800/60' : ''}`}
    >
      <span className={`mt-0.5 shrink-0 inline-flex h-7 w-7 items-center justify-center rounded-full ${config.iconBg}`}>
        {config.icon}
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm leading-snug ${isUnread ? 'font-semibold text-slate-900 dark:text-white' : 'font-medium text-slate-700 dark:text-gray-200'}`}>
            {notificacion.titulo}
          </p>
          <span className="shrink-0 text-[11px] text-slate-400 dark:text-gray-500 tabular-nums mt-0.5">
            {tiempoRelativo(notificacion.fechaOcurrencia)}
          </span>
        </div>
        {notificacion.descripcion && (
          <p className="mt-0.5 text-xs text-slate-500 dark:text-gray-400 line-clamp-2 leading-relaxed">
            {notificacion.descripcion}
          </p>
        )}
      </div>

      {isUnread && (
        <span className="mt-1.5 shrink-0 h-1.5 w-1.5 rounded-full bg-blue-500" aria-hidden="true" />
      )}
    </button>
  );
}

export function NotificacionesDropdown() {
  const {
    notificaciones, sinLeerCount, esCargando,
    cargarNotificaciones, marcarLeida, marcarTodasLeidas,
  } = useNotificaciones();

  const [isOpen, setIsOpen] = useState(false);
  const [, setTick] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const didLoadRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    const id = window.setInterval(() => setTick(t => t + 1), 30_000);
    return () => window.clearInterval(id);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !didLoadRef.current) {
      didLoadRef.current = true;
      void cargarNotificaciones();
    }
    if (!isOpen) didLoadRef.current = false;
  }, [isOpen, cargarNotificaciones]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node))
        setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <Tooltip text="Notificaciones" position="bottom">
        <span className="relative inline-flex">
          <Button
            iconOnly
            variant="ghost"
            size="md"
            aria-label="Notificaciones"
            onClick={() => setIsOpen(prev => !prev)}
            active={isOpen}
            icon={<Bell className="w-[18px] h-[18px]" />}
          />
          {sinLeerCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[0.75rem] h-3 px-[2px] bg-red-500 rounded-full border-2 border-white dark:border-gray-800 flex items-center justify-center pointer-events-none">
              <span className="sr-only">Tienes {sinLeerCount} notificaciones sin leer</span>
            </span>
          )}
        </span>
      </Tooltip>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-[480px] bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl shadow-xl z-50 flex flex-col">
          {/* Header fijo */}
          <div className="px-4 py-3 border-b border-slate-100 dark:border-gray-700 shrink-0">
            <div className="flex items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-slate-900 dark:text-white text-base">Notificaciones</h3>
                <p className="text-xs text-slate-500 dark:text-gray-400">
                  {sinLeerCount > 0 ? `${sinLeerCount} sin leer` : 'Al día, sin pendientes'}
                </p>
              </div>
              {sinLeerCount > 0 && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                  onClick={() => void marcarTodasLeidas()}
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Marcar todas como leídas
                </button>
              )}
            </div>
          </div>

          {/* Lista */}
          <div className="overflow-y-auto divide-y divide-slate-100 dark:divide-gray-700/60" style={{ maxHeight: '320px' }}>
            {esCargando && (
              <div className="px-4 py-6 text-center text-sm text-slate-500 dark:text-gray-400">
                Cargando...
              </div>
            )}

            {!esCargando && notificaciones.length === 0 && (
              <div className="px-4 py-6 text-center text-sm text-slate-500 dark:text-gray-400">
                <p className="font-medium text-slate-700 dark:text-gray-200 mb-1">
                  Sin notificaciones recientes
                </p>
                <p className="text-xs">Te avisaremos cuando haya algo importante.</p>
              </div>
            )}

            {!esCargando && notificaciones.length > 0 &&
              notificaciones.slice(0, 5).map(n => (
                <ItemNotificacion
                  key={n.id}
                  notificacion={n}
                  onMarcarLeida={(id) => void marcarLeida(id)}
                />
              ))
            }
          </div>

          {/* Footer fijo */}
          <div className="border-t border-slate-100 dark:border-gray-700 shrink-0">
            <button
              type="button"
              className="w-full px-4 py-2.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              onClick={() => { setIsOpen(false); navigate('/notificaciones'); }}
            >
              Ver todas las notificaciones
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


